import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Stack, TextField, Avatar, createFilterOptions } from '@mui/material';
import { find } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { DriverCompanyBase } from '~services/driver/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { useTypePartnerCompany, useAppSelector } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { getDefaultAvatar } from '~utils/user';
import {
  Subscription,
  AccessFeature,
  UserInfo,
  CustomAutocomplete,
} from '~components/index';
import { AccessFeatureAutoCompleteRef } from '~components/AccessFeature/AutoComplete/types';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';

import { MIN_PERIOD_MONTH, CURRENT_PERIOD_MONTH } from '../../utils';

import { useReportsUserSatifactionSectionContext } from '../Context';

const ReportsDownloadUserSatifactionFilters = (): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();
  const { user } = useAppSelector(selectAuth);

  const {
    filters: {
      currentPeriod: { value: currentPeriod, setValue: setCurrentPeriod },
      currentDriverId: { setValue: setCurrentDriverId },
      currentAssociatedCompanyId: { setValue: setCurrentAssociatedCompanyId },
      selectsData: {
        availablesDrivers: {
          data: availablesDrivers,
          loading: loadingAvailablesDrivers,
        },
        associatedCompanies: {
          data: associatedCompanies,
          loading: loadingAssocitedCompanies,
        },
      },
    },
  } = useReportsUserSatifactionSectionContext();

  const handleChangePeriod = useCallback(
    (newPeriod: Nullable<Date>) => {
      setCurrentPeriod(newPeriod);
    },
    [setCurrentPeriod],
  );

  const refDriverAutocomplete =
    useRef<AccessFeatureAutoCompleteRef<DriverCompanyBase, false>>(null);
  const handleChangeDriver = useCallback(
    (_event: SyntheticEvent, newDriver: Nullable<DriverCompanyBase>) => {
      setCurrentDriverId(newDriver?.id ?? null);
    },
    [setCurrentDriverId],
  );

  const refAssociatedCompaniesAutocomplete =
    useRef<CustomAutocompleteRef<AuthExtendedCompany, false>>(null);
  const handleChangeAssociatedCompanies = useCallback(
    (_event: SyntheticEvent, value: Nullable<AuthExtendedCompany>) => {
      setCurrentAssociatedCompanyId(value?.id ?? null);
    },
    [setCurrentAssociatedCompanyId],
  );

  const setDefaultAssociatedCompany = useCallback(() => {
    const currentCompanyId = user?.companyId ?? null;

    if (currentCompanyId && !loadingAssocitedCompanies) {
      const filterCurrentCompany = find(associatedCompanies, {
        id: currentCompanyId,
      });

      refAssociatedCompaniesAutocomplete.current?.setValue(
        filterCurrentCompany ?? null,
      );
    }
  }, [user, loadingAssocitedCompanies, associatedCompanies]);

  useEffect(() => {
    if (isCarrier) {
      setDefaultAssociatedCompany();
    }
  }, [isCarrier, setDefaultAssociatedCompany]);

  return (
    <Stack direction="row" spacing={2}>
      <Subscription.DataRetentionMin.DatePicker
        views={['month', 'year']}
        label="Seleccione el período"
        minDate={MIN_PERIOD_MONTH}
        maxDate={CURRENT_PERIOD_MONTH}
        value={currentPeriod}
        onChange={handleChangePeriod}
        renderInput={(props) => <TextField {...props} sx={{ width: 300 }} />}
      />

      {isCarrier && (
        <AccessFeature.AutoComplete
          validFeature={PlanFeaturesTypes.ReportAdvanced}
          showChip
          openAccessFeatureModalOnClickChip
          fullWidth
          innerRef={refDriverAutocomplete}
          options={availablesDrivers}
          loading={loadingAvailablesDrivers}
          label="Seleccione el conductor"
          name="currentDriver"
          onChange={handleChangeDriver}
          getOptionLabel={(option) => option.displayName}
          renderOption={(props, option) => (
            <li {...props}>
              <UserInfo
                name={option.displayName}
                urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
              />
            </li>
          )}
          startAdornment={(val) =>
            val && (
              <Avatar
                src={getDefaultAvatar(val.avatar ?? undefined)}
                alt={val?.displayName}
                sx={{ width: 20, height: 20 }}
              />
            )
          }
          filterOptions={createFilterOptions({
            ignoreAccents: true,
            ignoreCase: true,
            stringify: (option) => option.displayName.replace(',', ''),
          })}
          sx={{ width: 300 }}
        />
      )}

      <CustomAutocomplete
        fullWidth
        innerRef={refAssociatedCompaniesAutocomplete}
        options={associatedCompanies}
        loading={loadingAssocitedCompanies}
        label="Seleccione la empresa"
        name="associatedCompanies"
        onChange={handleChangeAssociatedCompanies}
        getOptionLabel={(option) => option.name}
        renderOption={(props, option) => (
          <li {...props}>
            <UserInfo name={option.name} urlAvatar={option.logoFile?.url} />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={val.logoFile?.url}
              alt={val?.name}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) => option.name.replace(',', ''),
        })}
        sx={{ width: 300, display: 'none' }}
      />
    </Stack>
  );
};

export default ReportsDownloadUserSatifactionFilters;
